// uplot-plugins.ts — small canvas-draw plugins shared by the chart components.
import type uPlot from 'uplot';
import type { Plugin } from 'uplot';
import { readCssVar } from './theme.js';

/** Always-visible dashed line at y = 0, so a negative Verrechnungskonto reads
 * unambiguously. Color: --border from the palette (src/styles/global.css). */
export function zeroLinePlugin(color = '#232c42'): Plugin {
  return {
    hooks: {
      draw: [
        (u: uPlot) => {
          const yPix = u.valToPos(0, 'y', true);
          if (yPix < u.bbox.top || yPix > u.bbox.top + u.bbox.height) return;
          const ctx = u.ctx;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(u.bbox.left, yPix);
          ctx.lineTo(u.bbox.left + u.bbox.width, yPix);
          ctx.stroke();
          ctx.restore();
        },
      ],
    },
  };
}

/** Vertical marker at a fixed x (unix seconds) — used for the crossover ("Break-even")
 * date. Color: the current --accent-strong theme preset (lib/theme.ts), kept distinct
 * from any series color. Default is a function (not a plain string) so it re-reads the
 * live CSS custom property on every call, matching whichever accent preset is active. */
export function verticalMarkerPlugin(getXValue: () => number | null, color = readCssVar('--accent-strong', '#3b82f6')): Plugin {
  return {
    hooks: {
      draw: [
        (u: uPlot) => {
          const xVal = getXValue();
          if (xVal == null) return;
          const xPix = u.valToPos(xVal, 'x', true);
          if (xPix < u.bbox.left || xPix > u.bbox.left + u.bbox.width) return;
          const ctx = u.ctx;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 4]);
          ctx.beginPath();
          ctx.moveTo(xPix, u.bbox.top);
          ctx.lineTo(xPix, u.bbox.top + u.bbox.height);
          ctx.stroke();
          ctx.restore();
        },
      ],
    },
  };
}

/** Small vertical dotted markers at several fixed x values (unix seconds) — used for
 * Sonderzahlungen (one-off extra contribution) months on the Verlauf chart. Deliberately
 * lighter/shorter than verticalMarkerPlugin's break-even line (dotted vs. dashed, --positive
 * from the palette) so it never reads as another break-even/crossover indicator. Cheap: one
 * extra draw-hook loop over a handful of dates, no new series/data. */
export function verticalMarkersPlugin(getXValues: () => number[], color = '#4ade80'): Plugin {
  return {
    hooks: {
      draw: [
        (u: uPlot) => {
          const xVals = getXValues();
          if (xVals.length === 0) return;
          const ctx = u.ctx;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([1, 3]);
          for (const xVal of xVals) {
            const xPix = u.valToPos(xVal, 'x', true);
            if (xPix < u.bbox.left || xPix > u.bbox.left + u.bbox.width) continue;
            ctx.beginPath();
            ctx.moveTo(xPix, u.bbox.top);
            ctx.lineTo(xPix, u.bbox.top + u.bbox.height);
            ctx.stroke();
          }
          ctx.restore();
        },
      ],
    },
  };
}

/* ---- 2D (x + y) chart zoom (SimulationChart, CompareChart, MonteCarloChart) --------
 * uPlot already gives two zoom gestures for free once `cursor.drag = { x: true, y: true }`
 * is set (every chart here now has that): dragging out a selection rectangle zooms BOTH
 * axes to exactly that rectangle (uPlot's own mouseUp handler — with both drag axes on
 * and no `drag.uni` configured, any drag is treated as "omni" and sets both scales from
 * the dragged box), and double-click resets via uPlot's own built-in autoScaleX (which,
 * because the y scale is left on its default `auto: true`, cascades into re-fitting y to
 * the now-full-again x window too — see setScales()/accScale() in uPlot's source for the
 * exact mechanism). This plugin adds exactly the two gestures uPlot does NOT implement
 * itself — desktop mouse wheel and mobile two-finger pinch — now on BOTH axes, each
 * independently clamped to that axis's own data range, and always applied via `u.batch`
 * so x and y commit in the same redraw (a non-batched pair of setScale calls would let
 * uPlot's auto-y-refit-on-x-change kick in between them and briefly show/settle on the
 * wrong y range). */

/** Reported on every x- or y-scale change (wheel here, pinch here, or uPlot's own
 * built-in drag-select / dblclick-reset — see the `setScale` hook below) so a host
 * component can render a single "Zoom zurücksetzen" affordance without reaching into
 * uPlot internals itself. `zoomed` is true when EITHER axis differs from the data's own
 * full range. `reset` re-reads the CURRENT data's full x/y range off the live uPlot
 * instance at CALL time (never a value captured back when zooming started), so it keeps
 * working correctly even if the chart's data changes while zoomed in (a re-run scenario,
 * a new Monte-Carlo draw, …). */
export interface ZoomState {
  zoomed: boolean;
  reset: () => void;
}

const ZOOM_EPS = 1e-6;

function xDataRange(u: uPlot): [number, number] | null {
  const xs = u.data[0] as unknown as number[] | undefined;
  if (!xs || xs.length === 0) return null;
  const first = xs[0];
  const last = xs[xs.length - 1];
  if (first == null || last == null) return null;
  return first <= last ? [first, last] : [last, first];
}

/** The y-axis analogue of xDataRange: min/max across every y-series' values (skipping
 * index 0, the shared x column), clamped to also include 0 — mirrors, value for value,
 * the `scales.y.range` callback every one of the three chart components already defines
 * (`[Math.min(0, initMin), Math.max(0, initMax)]`), so a full y-reset lines up exactly
 * with how the chart auto-ranges y on first load / a fresh data set, including keeping
 * the always-visible zero line (zeroLinePlugin) inside the reset range. */
function yDataRange(u: uPlot): [number, number] | null {
  let min = Infinity;
  let max = -Infinity;
  for (let s = 1; s < u.data.length; s++) {
    const col = u.data[s] as unknown as (number | null)[] | undefined;
    if (!col) continue;
    for (const v of col) {
      if (v == null) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return [Math.min(0, min), Math.max(0, max)];
}

/** Slides [min, max] to stay fully inside [dataMin, dataMax] without shrinking a span
 * that already fits (clamp-by-sliding, not clipping — clipping near an edge would also
 * silently shrink the visible window), and floors the span at `minSpan` so a wheel/pinch
 * gesture can't zoom in until the chart shows a meaningless sliver or, worse, inverts. */
function clampAxisRange(
  dataMin: number,
  dataMax: number,
  min: number,
  max: number,
  minSpan: number,
): [number, number] {
  const fullSpan = dataMax - dataMin;
  if (fullSpan <= 0) return [dataMin, dataMax];

  const span = Math.min(Math.max(max - min, minSpan), fullSpan);
  let lo = min;
  let hi = lo + span;
  if (lo < dataMin) {
    lo = dataMin;
    hi = lo + span;
  }
  if (hi > dataMax) {
    hi = dataMax;
    lo = hi - span;
  }
  return [lo, hi];
}

/** X floor: roughly 4 data points' worth of spacing — points are the natural "smallest
 * meaningful unit" on a monthly/yearly time series. Returns null only when the chart has
 * no data at all. */
function clampXRange(u: uPlot, min: number, max: number): [number, number] | null {
  const range = xDataRange(u);
  if (!range) return null;
  const [dataMin, dataMax] = range;
  const fullSpan = dataMax - dataMin;
  if (fullSpan <= 0) return [dataMin, dataMax];

  const xs = u.data[0] as unknown as number[];
  const avgSpacing = xs.length > 1 ? fullSpan / (xs.length - 1) : fullSpan;
  const minSpan = Math.min(fullSpan, Math.max(avgSpacing * 4, fullSpan / 1000));
  return clampAxisRange(dataMin, dataMax, min, max, minSpan);
}

/** Y floor: no natural "point spacing" concept for a continuous money axis, so this
 * floors the span at a small fraction (0.4 %) of the full y-range instead — enough to
 * stop a runaway wheel/pinch gesture from zooming into a flat, unreadable sliver. */
function clampYRange(u: uPlot, min: number, max: number): [number, number] | null {
  const range = yDataRange(u);
  if (!range) return null;
  const [dataMin, dataMax] = range;
  const fullSpan = dataMax - dataMin;
  const minSpan = fullSpan > 0 ? fullSpan / 250 : 0;
  return clampAxisRange(dataMin, dataMax, min, max, minSpan);
}

function touchMidAndDist(touches: TouchList): { midClientX: number; midClientY: number; dist: number } {
  const a = touches[0]!;
  const b = touches[1]!;
  return {
    midClientX: (a.clientX + b.clientX) / 2,
    midClientY: (a.clientY + b.clientY) / 2,
    dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
  };
}

/**
 * @param onChange Called with the current zoomed/reset state every time either scale
 * changes for ANY reason (wheel here, pinch here, or uPlot's own built-in drag-select /
 * dblclick-reset) — all of them funnel through uPlot's `setScale` hook, so this plugin
 * doesn't need to special-case which gesture fired.
 */
export function chartZoomPlugin(onChange: (state: ZoomState) => void): Plugin {
  let pinchStartDist: number | null = null;
  let pinchStartXMin: number | null = null;
  let pinchStartXMax: number | null = null;
  let pinchStartYMin: number | null = null;
  let pinchStartYMax: number | null = null;
  let pinchAnchorXVal: number | null = null;
  let pinchAnchorYVal: number | null = null;

  function reset(u: uPlot): void {
    const xRange = xDataRange(u);
    const yRange = yDataRange(u);
    if (!xRange && !yRange) return;
    u.batch(() => {
      if (xRange) u.setScale('x', { min: xRange[0], max: xRange[1] });
      if (yRange) u.setScale('y', { min: yRange[0], max: yRange[1] });
    });
  }

  function report(u: uPlot): void {
    const xRange = xDataRange(u);
    const yRange = yDataRange(u);
    const scX = u.scales.x;
    const scY = u.scales.y;
    const xZoomed =
      xRange != null &&
      scX != null &&
      scX.min != null &&
      scX.max != null &&
      (scX.min > xRange[0] + ZOOM_EPS || scX.max < xRange[1] - ZOOM_EPS);
    const yZoomed =
      yRange != null &&
      scY != null &&
      scY.min != null &&
      scY.max != null &&
      (scY.min > yRange[0] + ZOOM_EPS || scY.max < yRange[1] - ZOOM_EPS);
    onChange({ zoomed: xZoomed || yZoomed, reset: () => reset(u) });
  }

  /** Applies an independently-clamped zoom to both axes in a single batch (see the
   * section header comment for why batching matters here). Either axis's zoom is
   * skipped (left untouched) if that axis has no data / an invalid scale. */
  function applyZoom2D(
    u: uPlot,
    xAnchor: number,
    xRatio: number,
    newXSpan: number,
    yAnchor: number,
    yRatio: number,
    newYSpan: number,
  ): void {
    const cx = clampXRange(u, xAnchor - xRatio * newXSpan, xAnchor - xRatio * newXSpan + newXSpan);
    const cy = clampYRange(u, yAnchor - yRatio * newYSpan, yAnchor - yRatio * newYSpan + newYSpan);
    if (!cx && !cy) return;
    u.batch(() => {
      if (cx) u.setScale('x', { min: cx[0], max: cx[1] });
      if (cy) u.setScale('y', { min: cy[0], max: cy[1] });
    });
  }

  function onWheel(u: uPlot, e: WheelEvent): void {
    const scX = u.scales.x;
    const scY = u.scales.y;
    if (scX == null || scX.min == null || scX.max == null) return;
    if (scY == null || scY.min == null || scY.max == null) return;
    e.preventDefault();
    const rect = u.over.getBoundingClientRect();
    const xVal = u.posToVal(e.clientX - rect.left, 'x');
    const yVal = u.posToVal(e.clientY - rect.top, 'y');
    const xSpan = scX.max - scX.min;
    const ySpan = scY.max - scY.min;
    const xRatio = xSpan > 0 ? (xVal - scX.min) / xSpan : 0.5;
    const yRatio = ySpan > 0 ? (yVal - scY.min) / ySpan : 0.5;
    // deltaY > 0 (scroll down / pinch-out on a trackpad) zooms out; < 0 zooms in. ~15%
    // per notch reads as a natural, controllable step (matches common map/chart UIs).
    // Both axes zoom by the SAME factor on every notch, around their own cursor value —
    // that keeps the aspect-independent "zoom toward what's under the pointer" feel.
    const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
    applyZoom2D(u, xVal, xRatio, xSpan * factor, yVal, yRatio, ySpan * factor);
  }

  function onTouchStart(u: uPlot, e: TouchEvent): void {
    if (e.touches.length < 2) return;
    const scX = u.scales.x;
    const scY = u.scales.y;
    if (scX == null || scX.min == null || scX.max == null) return;
    if (scY == null || scY.min == null || scY.max == null) return;
    e.preventDefault();
    const rect = u.over.getBoundingClientRect();
    const { midClientX, midClientY, dist } = touchMidAndDist(e.touches);
    pinchStartDist = dist;
    pinchStartXMin = scX.min;
    pinchStartXMax = scX.max;
    pinchStartYMin = scY.min;
    pinchStartYMax = scY.max;
    pinchAnchorXVal = u.posToVal(midClientX - rect.left, 'x');
    pinchAnchorYVal = u.posToVal(midClientY - rect.top, 'y');
  }

  function onTouchMove(u: uPlot, e: TouchEvent): void {
    if (
      e.touches.length < 2 ||
      pinchStartDist == null ||
      pinchStartDist <= 0 ||
      pinchAnchorXVal == null ||
      pinchAnchorYVal == null ||
      pinchStartXMin == null ||
      pinchStartXMax == null ||
      pinchStartYMin == null ||
      pinchStartYMax == null
    ) {
      return;
    }
    e.preventDefault();
    const { dist } = touchMidAndDist(e.touches);
    if (dist <= 0) return;
    const startXSpan = pinchStartXMax - pinchStartXMin;
    const startYSpan = pinchStartYMax - pinchStartYMin;
    const xRatio = startXSpan > 0 ? (pinchAnchorXVal - pinchStartXMin) / startXSpan : 0.5;
    const yRatio = startYSpan > 0 ? (pinchAnchorYVal - pinchStartYMin) / startYSpan : 0.5;
    // fingers spreading apart (dist > start) -> zoom IN -> smaller span, and vice versa.
    // Both axes scale by the same distance ratio — one physical pinch, one zoom factor.
    const scale = pinchStartDist / dist;
    applyZoom2D(u, pinchAnchorXVal, xRatio, startXSpan * scale, pinchAnchorYVal, yRatio, startYSpan * scale);
  }

  function onTouchEnd(_u: uPlot, e: TouchEvent): void {
    if (e.touches.length < 2) {
      pinchStartDist = null;
      pinchStartXMin = null;
      pinchStartXMax = null;
      pinchStartYMin = null;
      pinchStartYMax = null;
      pinchAnchorXVal = null;
      pinchAnchorYVal = null;
    }
  }

  let onWheelBound: ((e: WheelEvent) => void) | null = null;
  let onTouchStartBound: ((e: TouchEvent) => void) | null = null;
  let onTouchMoveBound: ((e: TouchEvent) => void) | null = null;
  let onTouchEndBound: ((e: TouchEvent) => void) | null = null;

  return {
    hooks: {
      ready: [
        (u: uPlot) => {
          onWheelBound = (e) => onWheel(u, e);
          onTouchStartBound = (e) => onTouchStart(u, e);
          onTouchMoveBound = (e) => onTouchMove(u, e);
          onTouchEndBound = (e) => onTouchEnd(u, e);
          // passive: false on all four — wheel and the 2-finger touch gestures need to
          // preventDefault (stop page scroll / native pinch-zoom) while single-finger
          // touch is left completely alone so `touch-action: pan-y` on the chart host
          // (global.css) keeps handling one-finger vertical page scroll natively.
          u.over.addEventListener('wheel', onWheelBound, { passive: false });
          u.over.addEventListener('touchstart', onTouchStartBound, { passive: false });
          u.over.addEventListener('touchmove', onTouchMoveBound, { passive: false });
          u.over.addEventListener('touchend', onTouchEndBound, { passive: false });
          u.over.addEventListener('touchcancel', onTouchEndBound, { passive: false });
          report(u);
        },
      ],
      setScale: [
        (u: uPlot, key: string) => {
          if (key === 'x' || key === 'y') report(u);
        },
      ],
      destroy: [
        (u: uPlot) => {
          if (onWheelBound) u.over.removeEventListener('wheel', onWheelBound);
          if (onTouchStartBound) u.over.removeEventListener('touchstart', onTouchStartBound);
          if (onTouchMoveBound) u.over.removeEventListener('touchmove', onTouchMoveBound);
          if (onTouchEndBound) {
            u.over.removeEventListener('touchend', onTouchEndBound);
            u.over.removeEventListener('touchcancel', onTouchEndBound);
          }
        },
      ],
    },
  };
}
